import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Package, MapPin, Truck, Home, ArrowRight, BookOpen, Copy, Check } from "lucide-react";

const STEPS = [
  { key: "confirmed",  icon: CheckCircle, label: "Confirmed" },
  { key: "processing", icon: Package,     label: "Processing" },
  { key: "shipped",    icon: Truck,       label: "Shipped" },
  { key: "delivered",  icon: Home,        label: "Delivered" },
];

export default function OrderSuccess() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { paymentId, address, items, total, orderId } = state || {};
  const [copied, setCopied] = useState(false);

  const copyId = () => {
    if (!paymentId) return;
    navigator.clipboard?.writeText(paymentId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shipping = 9900;
  const subtotal = total ? total - shipping : 0;

  return (
    <div style={{ minHeight: "calc(100vh - var(--nav-h))", background: "var(--cream)", padding: "2.5rem 1.25rem 6rem" }}>
      <div style={{ maxWidth: "640px", margin: "0 auto" }}>

        {/* ── Success hero ── */}
        <div style={{ textAlign: "center", marginBottom: "2.5rem", animation: "fadeUp 0.5s ease both" }}>
          <div style={{
            width: "80px", height: "80px", borderRadius: "50%",
            background: "var(--success-faint)",
            border: "2px solid var(--success)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem",
            animation: "popIn 0.45s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            <CheckCircle size={38} color="var(--success)" strokeWidth={2.5} />
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", color: "var(--ink)", fontSize: "clamp(1.6rem,4vw,2.2rem)", fontWeight: 700, marginBottom: "0.5rem" }}>
            Order Confirmed!
          </h1>
          <p style={{ color: "var(--muted)", fontSize: "0.92rem", lineHeight: 1.7, maxWidth: "400px", margin: "0 auto 0.75rem" }}>
            Thank you for your purchase. We'll start processing your order shortly.
          </p>
          {orderId && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--cream-2)", border: "1px solid var(--dust-2)", borderRadius: "var(--radius-full)", padding: "5px 14px" }}>
              <span style={{ fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Order</span>
              <span style={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700, color: "var(--ink)" }}>#{orderId.slice(-8).toUpperCase()}</span>
            </div>
          )}
        </div>

        {/* ── Progress tracker ── */}
        <div style={{
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: "var(--radius-lg)", padding: "1.5rem",
          marginBottom: "1.25rem", animation: "fadeUp 0.5s 0.1s ease both",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", position: "relative" }}>
            {/* connector line */}
            <div style={{
              position: "absolute", top: "20px", left: "calc(12.5% + 0px)", right: "calc(12.5% + 0px)",
              height: "2px", background: "var(--dust-2)", zIndex: 0,
            }} />
            {STEPS.map((step, i) => {
              const done = i === 0;
              const Icon = step.icon;
              return (
                <div key={step.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", position: "relative", zIndex: 1 }}>
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "50%",
                    background: done ? "var(--success)" : "var(--card-bg)",
                    border: `2px solid ${done ? "var(--success)" : "var(--dust-2)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: done ? "#fff" : "var(--muted-2)",
                    transition: "all 0.3s",
                  }}>
                    <Icon size={16} strokeWidth={done ? 2.5 : 1.8} />
                  </div>
                  <span style={{ fontSize: "0.68rem", fontWeight: done ? 700 : 500, color: done ? "var(--ink)" : "var(--muted-2)", textAlign: "center", lineHeight: 1.3 }}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Order details card ── */}
        {paymentId && (
          <div style={{
            background: "var(--card-bg)", border: "1px solid var(--card-border)",
            borderRadius: "var(--radius-lg)", overflow: "hidden",
            boxShadow: "var(--shadow)", marginBottom: "1.25rem",
            animation: "fadeUp 0.5s 0.15s ease both",
          }}>

            {/* Payment ID row */}
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--card-border)", background: "var(--cream-2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "3px" }}>Payment ID</div>
                <div style={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{paymentId}</div>
              </div>
              <button onClick={copyId} title="Copy" style={{
                flexShrink: 0, width: "30px", height: "30px", borderRadius: "var(--radius)",
                border: "1px solid var(--dust-2)", background: copied ? "var(--success-faint)" : "var(--card-bg)",
                color: copied ? "var(--success)" : "var(--muted)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s",
              }}>
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>

            {/* Items */}
            {items?.length > 0 && (
              <div style={{ padding: "1.25rem", borderBottom: address ? "1px solid var(--card-border)" : "none" }}>
                <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Package size={11} /> Items Ordered
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {items.map((item, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {/* mini cover placeholder */}
                      <div style={{
                        width: "36px", height: "50px", borderRadius: "3px", flexShrink: 0,
                        background: `linear-gradient(135deg, ${item.product?.colorFrom || "#1A1108"}, ${item.product?.colorTo || "#C8941F"})`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <BookOpen size={14} color="rgba(255,255,255,0.7)" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.product?.title || item.title}</div>
                        {item.product?.author && <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{item.product.author}</div>}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--ink)" }}>₹{((item.product?.price || 0) * item.quantity / 100).toFixed(0)}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>qty {item.quantity}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--card-border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.83rem", color: "var(--muted)", marginBottom: "4px" }}>
                    <span>Subtotal</span><span>₹{(subtotal / 100).toFixed(0)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.83rem", color: "var(--muted)", marginBottom: "10px" }}>
                    <span>Shipping</span><span>{shipping > 0 ? `₹${(shipping/100).toFixed(0)}` : "Free"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 700, color: "var(--ink)", fontSize: "0.95rem" }}>Total Paid</span>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.5rem", color: "var(--gold)" }}>₹{(total / 100).toFixed(0)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {address && (
              <div style={{ padding: "1rem 1.25rem", background: "var(--cream-2)" }}>
                <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <MapPin size={11} /> Delivering To
                </div>
                <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--ink)" }}>{address.name}</div>
                <div style={{ fontSize: "0.82rem", color: "var(--muted)", lineHeight: 1.6, marginTop: "2px" }}>
                  {address.line1}{address.line2 ? `, ${address.line2}` : ""}, {address.city}, {address.state} — {address.pincode}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", animation: "fadeUp 0.5s 0.2s ease both" }}>
          <button onClick={() => navigate(orderId ? `/orders/${orderId}` : "/orders")} style={{
            width: "100%", padding: "13px 24px",
            background: "linear-gradient(135deg, var(--gold), var(--gold-2))", color: "#fff",
            border: "none", borderRadius: "var(--radius-full)", fontFamily: "var(--font-body)",
            fontWeight: 700, fontSize: "0.92rem", cursor: "pointer",
            boxShadow: "var(--shadow-gold)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}>
            <Package size={15} /> Track Your Order
          </button>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => navigate("/store")} style={{
              flex: 1, padding: "11px 16px", background: "none", color: "var(--ink)",
              border: "1px solid var(--dust-2)", borderRadius: "var(--radius-full)",
              fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.88rem",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            }}>
              Store <ArrowRight size={13} />
            </button>
            <button onClick={() => navigate("/")} style={{
              flex: 1, padding: "11px 16px", background: "none", color: "var(--ink)",
              border: "1px solid var(--dust-2)", borderRadius: "var(--radius-full)",
              fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.88rem",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            }}>
              <BookOpen size={13} /> Library
            </button>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.6); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
